import React, { useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, InputNumber, Modal, Space, Typography, message } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { formatCents } from "../../../serviceProvider/components/accounting/shared/money";
import * as api from "./salesApi";

const percentOf = (cents, percent) => Math.round((Number(cents) * Number(percent)) / 100);

/**
 * "Abschlag nach Leistungsstand": the user enters the cumulative progress per offer position;
 * the server creates a draft Abschlagsrechnung for the progress not billed yet (ABS-5).
 */
const ProgressAdvanceModal = ({ open, offer, onClose, onCreated }) => {
  const fmtMoney = cents => formatCents(cents, { currency: offer?.currency });
  const { t } = useTranslation("accounting");
  const [state, setState] = useState(null);
  const [percents, setPercents] = useState({});
  const [issueDate, setIssueDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !offer?._id) return;
    setLoading(true);
    setIssueDate(null);
    api
      .progressState(offer._id)
      .then(res => {
        setState(res.data);
        const init = {};
        (res.data.positions || []).forEach(p => {
          init[p._id] = p.percent;
        });
        setPercents(init);
      })
      .catch(err => message.error(api.errorText(err)))
      .finally(() => setLoading(false));
  }, [open, offer?._id]);

  const rows = useMemo(
    () =>
      (state?.positions || []).map(p => {
        const pct = percents[p._id] ?? p.percent;
        const delta = percentOf(p.net, pct) - p.billedNet;
        return { ...p, newPercent: pct, delta };
      }),
    [state, percents]
  );
  const net =
    rows.reduce((s, r) => s + Math.max(0, r.delta), 0) - Math.max(0, state?.unattributed || 0);
  const invalid = rows.some(r => r.delta < 0);

  const submit = async () => {
    const progress = rows
      .filter(r => r.delta > 0)
      .map(r => ({ offerItemId: r._id, percent: r.newPercent }));
    if (!progress.length) {
      message.warning(t("Enter a progress above what is already billed"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.advanceFromProgress(offer._id, {
        progress,
        issueDate: issueDate ? issueDate.format("YYYY-MM-DD") : null
      });
      message.success(t("Draft Abschlagsrechnung created"));
      onCreated?.(res.data.invoice);
      onClose();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t("Position"),
      dataIndex: "description",
      mobile: "title",
      render: (v, r) => (
        <span>
          {r.index + 1}. {v}
        </span>
      )
    },
    {
      title: t("Net"),
      dataIndex: "net",
      align: "end",
      render: v => fmtMoney(v)
    },
    {
      title: t("Billed so far"),
      dataIndex: "percent",
      align: "end",
      render: (v, r) => (
        <span>
          {v} % · {fmtMoney(r.billedNet)}
        </span>
      )
    },
    {
      title: t("Progress (cumulative %)"),
      key: "newPercent",
      render: (_, r) => (
        <InputNumber
          min={0}
          max={100}
          step={5}
          value={r.newPercent}
          status={r.delta < 0 ? "error" : undefined}
          onChange={v => setPercents(prev => ({ ...prev, [r._id]: v ?? 0 }))}
          addonAfter="%"
          style={{ width: 130 }}
        />
      )
    },
    {
      title: t("This Abschlag"),
      dataIndex: "delta",
      align: "end",
      mobile: "subtitle",
      render: v => (
        <Typography.Text type={v < 0 ? "danger" : undefined} strong={v > 0}>
          {fmtMoney(Math.max(0, v))}
        </Typography.Text>
      )
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={t("Create draft")}
      okButtonProps={{ disabled: invalid || net <= 0, loading: saving }}
      title={`${t("Abschlag by progress")} · ${offer?.offerNumber || offer?.quotationNumber || ""}`}
      width={900}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Typography.Text type="secondary">
          {t(
            "Enter the cumulative progress of each position. Earlier Abschläge are deducted automatically."
          )}
        </Typography.Text>
        {state?.hasFinal ? (
          <Alert type="warning" showIcon message={t("This offer already has a Schlussrechnung.")} />
        ) : null}
        {invalid ? (
          <Alert
            type="error"
            showIcon
            message={t("Progress cannot be lower than what is already billed.")}
          />
        ) : null}
        <ResponsiveTable
          rowKey="_id"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={rows}
          columns={columns}
        />
        {state?.unattributed > 0 ? (
          <Typography.Text type="secondary">
            {t("Earlier Abschläge without positions are deducted")}: −{fmtMoney(state.unattributed)}
          </Typography.Text>
        ) : null}
        <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
          <Space>
            <span>{t("Invoice date")}</span>
            <DatePicker
              value={issueDate}
              onChange={setIssueDate}
              format="DD.MM.YYYY"
              placeholder={t("On sending")}
              disabledDate={d => d && d.isAfter(dayjs().add(1, "year"))}
            />
          </Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("Net")}: {fmtMoney(Math.max(0, net))}
          </Typography.Title>
        </Space>
      </Space>
    </Modal>
  );
};

export default ProgressAdvanceModal;
